import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import { getAssignments, saveAssignments } from '../services/api';
import { toLocalISODate } from '../utils/dateUtils';

export default function Assignments() {
  const { vehicles, drivers, canManageAssignments, isAdmin } = useApp();
  const [date, setDate] = useState(() => toLocalISODate());
  const [rows, setRows] = useState({});
  const [activeTrips, setActiveTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const activeByVehicle = useMemo(() => {
    const map = new Map();
    activeTrips.forEach((t) => {
      if (t.vehicleId) map.set(String(t.vehicleId), t);
    });
    return map;
  }, [activeTrips]);

  const activeByDriver = useMemo(() => {
    const map = new Map();
    activeTrips.forEach((t) => {
      if (t.driverId) map.set(String(t.driverId), t);
    });
    return map;
  }, [activeTrips]);

  const vehicleList = useMemo(
    () => [...vehicles].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [vehicles]
  );

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAssignments(date);
      const list = Array.isArray(res?.assignments) ? res.assignments : [];
      const trips = Array.isArray(res?.activeTrips) ? res.activeTrips : [];
      const next = {};
      list.forEach((a) => {
        const vid = a.vehicle?._id || a.vehicle;
        if (vid) next[String(vid)] = String(a.driver?._id || a.driver || '');
      });
      // Vehicles currently out always show who is driving them right now
      trips.forEach((t) => {
        if (t.vehicleId && t.driverId) {
          next[String(t.vehicleId)] = String(t.driverId);
        }
      });
      setRows(next);
      setActiveTrips(trips);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load assignments');
      setRows({});
      setActiveTrips([]);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const setDriverForVehicle = (vehicleId, driverId) => {
    setRows((prev) => {
      const next = { ...prev };
      if (!driverId) {
        delete next[String(vehicleId)];
      } else {
        next[String(vehicleId)] = driverId;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!canManageAssignments) {
      toast.error('Sign in as dispatcher or admin to save assignments.');
      return;
    }
    const assignments = Object.entries(rows)
      .filter(([, driverId]) => driverId)
      .map(([vehicle, driver]) => ({ vehicle, driver }));

    setSaving(true);
    try {
      await saveAssignments(date, assignments);
      toast.success(`Assignments saved for ${date}`);
      await loadAssignments();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  const driverLabel = (d) => {
    const active = activeByDriver.get(String(d._id));
    if (!active) return d.name;
    return `${d.name} (out on ${active.vehicleName || 'vehicle'})`;
  };

  return (
    <>
      <div className="page-header">
        <h2>Daily Assignments</h2>
        <p>
          Assign which driver takes each vehicle before Vehicle Out. One driver may be
          assigned to multiple vehicles the same day (e.g. Celerio first, then Wagon R).
          If a driver is already out on one vehicle, you can still assign them to their
          <strong> next</strong> vehicle so everyone knows where they go after vehicle-in.
          Only the vehicle currently on trip is locked to that driver.
          {canManageAssignments ? '' : ' View only — sign in as dispatcher or admin to edit.'}
        </p>
      </div>

      <div className="card" style={{ maxWidth: 900 }}>
        <div className="form-grid cols2" style={{ marginBottom: 16 }}>
          <div className="form-group">
            <label>Assignment date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          {canManageAssignments && (
            <div className="form-group" style={{ alignSelf: 'end' }}>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving ? 'Saving…' : 'Save assignments'}
              </button>
            </div>
          )}
        </div>

        {!loading && activeTrips.length > 0 && (
          <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
            {activeTrips.length} vehicle{activeTrips.length === 1 ? '' : 's'} currently on trip.
            You can assign those drivers to their next vehicle on any other row below.
          </p>
        )}

        {loading ? (
          <p className="text-muted">Loading assignments…</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Plate</th>
                  <th>Assigned driver</th>
                </tr>
              </thead>
              <tbody>
                {vehicleList.map((v) => {
                  const vid = String(v._id);
                  const onTrip = activeByVehicle.get(vid);
                  const vehicleLocked = Boolean(onTrip);
                  const assignedDriverId = rows[vid] || '';
                  const assignedDriver = drivers.find((d) => String(d._id) === assignedDriverId);
                  const isNextAssignment = vehicleLocked === false
                    && assignedDriverId
                    && activeByDriver.has(assignedDriverId);

                  return (
                    <tr key={v._id}>
                      <td>{v.name}</td>
                      <td className="mono text-muted">{v.plate}</td>
                      <td>
                        {vehicleLocked ? (
                          <div>
                            <strong>{onTrip.driverName || assignedDriver?.name || '—'}</strong>
                            <span className="text-muted text-sm" style={{ display: 'block', marginTop: 4 }}>
                              Currently out on this vehicle — locked until vehicle-in
                            </span>
                          </div>
                        ) : (
                          <>
                            <select
                              value={assignedDriverId}
                              onChange={(e) => setDriverForVehicle(v._id, e.target.value)}
                              disabled={!canManageAssignments}
                            >
                              <option value="">— Not assigned —</option>
                              {drivers.map((d) => (
                                <option key={d._id} value={d._id}>{driverLabel(d)}</option>
                              ))}
                            </select>
                            {isNextAssignment && (
                              <span className="text-muted text-sm" style={{ display: 'block', marginTop: 4 }}>
                                Next vehicle after {activeByDriver.get(assignedDriverId)?.vehicleName || 'current trip'}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!canManageAssignments && (
          <p className="text-muted text-sm" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => window.location.assign('/login')}
            >
              Sign in
            </button>
            {' '}as dispatcher (Haider) or admin to edit.
          </p>
        )}

        {isAdmin && (
          <p className="text-muted text-sm" style={{ marginTop: 8 }}>
            Admins can also use Manage for vehicles and drivers; this page is only for daily pairing.
          </p>
        )}
      </div>
    </>
  );
}
